import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  onAuthStateChanged,
  PhoneAuthProvider, 
  PhoneMultiFactorGenerator, 
  getMultiFactorResolver, 
  RecaptchaVerifier
} from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, getDocs, setDoc } from 'firebase/firestore';
import './AdminLogin.css';

const AdminMfaPage = () => {
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();
  const resolverRef = useRef(null);
  const verifierRef = useRef(null);

  const storedEmail = sessionStorage.getItem('mfa.email');
  const storedPassword = sessionStorage.getItem('mfa.password');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        navigate('/admin/dashboard');
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    if (!storedEmail || !storedPassword) {
      navigate('/admin/login');
      return;
    }

    // Solo inicializar el verifier si no existe
    if (!verifierRef.current) {
      auth.settings.appVerificationDisabledForTesting = true;
      verifierRef.current = new RecaptchaVerifier(auth, 'mfa-recaptcha', {
        size: 'invisible',
      });
    }

    sendCode();

    return () => {
      if (verifierRef.current) {
        try { verifierRef.current.clear(); } catch (_) {}
        verifierRef.current = null;
      }
    };
  }, []);

  const sendCode = async () => {
    setLoading(true);
    setError('');

    try {
      // Intentamos el login para obtener el resolver
      await signInWithEmailAndPassword(auth, storedEmail, storedPassword);
      // Si entra aquí sin error, es que el MFA ya no era requerido (raro pero posible)
      navigate('/admin/dashboard');
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(auth, err);
          resolverRef.current = resolver;

          const phoneHint = resolver.hints.find(h => h.factorId === 'phone');

          if (!phoneHint) {
            setError('No phone factor enrolled');
            setLoading(false);
            return;
          }

          setPhoneNumber(phoneHint.phoneNumber || '');

          const provider = new PhoneAuthProvider(auth);
          // Usamos el verifierRef directamente
          const vid = await provider.verifyPhoneNumber({
            multiFactorHint: phoneHint,
            session: resolver.session,
          }, verifierRef.current);

          setVerificationId(vid); // Guardamos en el ESTADO
          setLoading(false);
        } catch (mfaErr) {
          setError('MFA error: ' + mfaErr.message);
          setLoading(false);
        }
      } else {
        setError(err.message);
        setLoading(false);
      }
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Enter 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    // Usamos el verificationId del estado
    if (!verificationId || !resolverRef.current) {
      setError('Session expired. Please click "Back to Login" and try again.');
      setLoading(false);
      return;
    }

    try {
      // CORRECCIÓN CLAVE:
      // En lugar de PhoneAuthProvider.assertion, usamos PhoneAuthProvider.credential
      const credential = PhoneAuthProvider.credential(verificationId, code);
      
      // En lugar de PhoneMultiFactorGenerator.assertion, usamos .assertion
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      
      const result = await resolverRef.current.resolveSignIn(assertion);

      if (result.user) {
        sessionStorage.removeItem('mfa.email');
        sessionStorage.removeItem('mfa.password');
        
        // Load user data and locations
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // If no selected location, set the first one
          if (!userData.selectedLocationId) {
            const locationsSnapshot = await getDocs(collection(db, 'users', result.user.uid, 'locations'));
            if (!locationsSnapshot.empty) {
              const firstLocation = locationsSnapshot.docs[0];
              await setDoc(doc(db, 'users', result.user.uid), {
                selectedLocationId: firstLocation.id
              }, { merge: true });
            }
          }
        }
        
        navigate('/admin/dashboard');
      }
    } catch (verifyErr) {
      console.error("Error detallado:", verifyErr);
      setLoading(false);
      setError('Invalid code or session expired.');
    }
  };

  // Renderizado (se mantiene igual, pero agregamos el spinner solo si no hay VID)
  return (
    <div className="admin-login-page">
      <div id="mfa-recaptcha"></div>
      <div className="login-box">
        <div className="card card-primary card-outline">
          <div className="card-body login-card-body">
            <div className="text-center mb-3">
              <i className="fas fa-shield-alt fa-3x text-success"></i>
              <h3 className="login-heading">Two-Factor Auth</h3>
              <p className="login-subheading">
                {verificationId && phoneNumber
                  ? `Code sent to ${phoneNumber}`
                  : 'Sending verification code...'}
              </p>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {loading && !verificationId ? (
              <div className="text-center">
                <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
                <p>Sending code...</p>
              </div>
            ) : (
              <>
                <div className="input-group mb-3">
                  <input
                    type="text"
                    className="form-control text-center"
                    style={{ fontSize: '24px', letterSpacing: '5px' }}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <div className="row">
                  <div className="col-12">
                    <button 
                      className="btn btn-primary btn-block"
                      onClick={handleVerify}
                      disabled={loading || code.length !== 6}
                    >
                      {loading ? 'Verifying...' : 'Verify Code'}
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="row mt-3">
              <div className="col-12 text-center">
                <button 
                  className="btn btn-link"
                  onClick={() => { sessionStorage.clear(); navigate('/admin/login'); }}
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMfaPage;